import { Decimal } from "decimal.js";

// Match the global precision used elsewhere in the app (see helpers/calc.js).
// We always pass an explicit rounding mode below so behaviour does not depend
// on Decimal's mutable global `rounding` setting.
Decimal.set({ precision: 20 });

export const TAX_ROUND = Object.freeze({
  NEAREST: "nearest",
  NO: "no",
  ORDER_LEVEL_DISTRIBUTED: "order_level_distributed",
});

/**
 * Coerce any value into a finite JS number, defaulting to 0.
 * Mirrors the defensive `toNumber` used in promotion-service.js.
 */
export function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Canonical money rounding: round to 2 decimal places, HALF_UP (ties away
 * from zero). This is the Decimal.js semantics used by helpers/calc.js
 * (`roundToTwo`, `getItemSubtotal`, `getOrderSubTotal`, `getOrderGrantTotal`)
 * and by `setTaxRound`'s "nearest" mode.
 *
 * This is the RECOMMENDED rounding for all new money math.
 */
export function round2(value) {
  return parseFloat(new Decimal(toNumber(value)).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toString());
}

/**
 * Tax-aware money rounding. Honors the store's `tax_round` setting:
 *   - "no": preserve full precision (no rounding)
 *   - everything else ("nearest" / "order_level_distributed"): round2 HALF_UP
 *
 * Replaces the duplicated logic in helpers/calc.js `setTaxRound` and
 * promotion-service.js `roundMoney` (without their float quirk — see
 * roundMoneyLegacyEpsilon for the exact legacy promotion behaviour).
 */
export function roundMoney(value, taxRound) {
  if (taxRound === TAX_ROUND.NO) return toNumber(value);
  return round2(value);
}

/**
 * EXACT legacy reproduction of promotion-service.js `roundMoney`:
 *   Math.round((v + Number.EPSILON) * 100) / 100
 * with a `tax_round === "no"` passthrough.
 *
 * Kept separate from `round2` because Math.round breaks ties toward +Infinity
 * (so negative .xx5 values round differently than Decimal HALF_UP). Promotion
 * discounts are non-negative in practice, so for those inputs this is
 * equivalent to round2, but we preserve the precise algorithm to avoid any
 * behavioural change on the promotion code path.
 */
export function roundMoneyLegacyEpsilon(value, taxRound) {
  const v = toNumber(value);
  if (taxRound === TAX_ROUND.NO) return v;
  return Math.round((v + Number.EPSILON) * 100) / 100;
}

/**
 * EXACT legacy reproduction of the `roundToTwoDecimalPlaces` helpers found in
 * helpers/calc.js (`handlingRoundOffCalculations`) and orderMixin.js:
 *   Math.round(num * 100) / 100
 *
 * Differs from round2 only on negative half-cent ties (Math.round rounds
 * toward +Infinity). Preserved verbatim for the discount-distribution path.
 */
export function roundToTwoDecimalPlaces(value) {
  return Math.round(toNumber(value) * 100) / 100;
}
